export type ServiceCategory = 'backend' | 'frontend' | 'infrastructure';

export type ServiceStatusType = 'healthy' | 'unhealthy' | 'checking';

export interface ServiceStatus {
  name: string;
  port: number;
  category: ServiceCategory;
  status: ServiceStatusType;
  responseTime: number | null;
  lastChecked: string;
  error: string | null;
}

export interface AlertPayload {
  type: 'down' | 'recovered';
  services: ServiceStatus[];
}

export interface ConnectionState {
  isConnected: boolean;
  isReconnecting: boolean;
  lastConnected: string | null;
}

export interface NotificationSettings {
  enabled: boolean;
  soundEnabled: boolean;
  permission: NotificationPermission;
}

// Group services by category
export function groupByCategory(
  services: ServiceStatus[],
): Record<ServiceCategory, ServiceStatus[]> {
  return services.reduce(
    (acc, service) => {
      if (!acc[service.category]) {
        acc[service.category] = [];
      }
      acc[service.category].push(service);
      return acc;
    },
    {
      backend: [],
      frontend: [],
      infrastructure: [],
    } as Record<ServiceCategory, ServiceStatus[]>,
  );
}

// Calculate health summary
export function calculateHealthSummary(services: ServiceStatus[]): {
  total: number;
  healthy: number;
  unhealthy: number;
  checking: number;
  percentage: number;
} {
  const total = services.length;
  const healthy = services.filter((s) => s.status === 'healthy').length;
  const unhealthy = services.filter((s) => s.status === 'unhealthy').length;
  const checking = services.filter((s) => s.status === 'checking').length;
  const percentage = total > 0 ? Math.round((healthy / total) * 100) : 0;

  return { total, healthy, unhealthy, checking, percentage };
}

// Format response time
export function formatResponseTime(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Get status color class
export function getStatusColor(status: ServiceStatusType): string {
  switch (status) {
    case 'healthy':
      return 'bg-green-500';
    case 'unhealthy':
      return 'bg-red-500';
    case 'checking':
      return 'bg-gray-400';
    default:
      return 'bg-gray-400';
  }
}

// Get badge variant for ui-kit Badge component
export function getBadgeVariant(
  status: ServiceStatusType,
): 'success' | 'destructive' | 'secondary' | 'warning' {
  switch (status) {
    case 'healthy':
      return 'success';
    case 'unhealthy':
      return 'destructive';
    case 'checking':
      return 'secondary';
    default:
      return 'secondary';
  }
}

// Category display names
export const categoryLabels: Record<ServiceCategory, string> = {
  backend: 'Backend Services',
  frontend: 'Frontend Apps',
  infrastructure: 'Infrastructure',
};

// Category icons (Lucide icon names)
export const categoryIcons: Record<ServiceCategory, string> = {
  backend: 'Server',
  frontend: 'Monitor',
  infrastructure: 'Database',
};
